import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import {
  getMyVenueGroupStatus, createVenueGroup, addVenueToGroup,
  removeVenueFromGroup, applyVenueGroupReferralCode, type VenueGroupStatus,
} from "@/lib/venue-groups.functions";
import { supabase } from "@/integrations/supabase/client";
import { COMPANY_URL } from "@/lib/brand";
import { ReserveHeader } from "@/components/ReserveHeader";
import { Link } from "@tanstack/react-router";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/venue/group")({
  head: () => ({ meta: [{ title: "Venue Group — The Hospitality Reserve" }] }),
  component: VenueGroupPage,
});

function VenueGroupPage() {
  const [status, setStatus] = useState<VenueGroupStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");

  async function refresh() {
    try { setStatus(await getMyVenueGroupStatus()); } finally { setLoading(false); }
  }
  useEffect(() => { void refresh(); }, []);

  // Real-time updates whenever a referral changes for this group.
  useEffect(() => {
    const groupId = status?.group?.id;
    if (!groupId) return;
    const channel = supabase
      .channel(`venue-group-${groupId}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "venue_group_referrals", filter: `referring_group_id=eq.${groupId}` }, () => void refresh())
      .on("postgres_changes", { event: "*", schema: "public", table: "venue_group_members", filter: `group_id=eq.${groupId}` }, () => void refresh())
      .subscribe();
    return () => { void supabase.removeChannel(channel); };
  }, [status?.group?.id]);

  const shareUrl = useMemo(() => {
    if (!status?.group?.referral_code) return "";
    return `${COMPANY_URL}/auth?ref_group=${encodeURIComponent(status.group.referral_code)}`;
  }, [status?.group?.referral_code]);

  const rewarded = useMemo(
    () => (status?.referrals_sent ?? []).filter((r) => r.status === "rewarded").length,
    [status?.referrals_sent],
  );
  const pending = useMemo(
    () => (status?.referrals_sent ?? []).filter((r) => r.status !== "rewarded").length,
    [status?.referrals_sent],
  );
  const creditsEarned = useMemo(
    () => (status?.referrals_sent ?? []).reduce((s, r) => s + (r.reward_waivers_granted ?? 0), 0),
    [status?.referrals_sent],
  );

  async function onCreate() {
    const r = await createVenueGroup({ data: { name } });
    if ("error" in r) toast.error(r.error); else { toast.success("Group created"); setName(""); void refresh(); }
  }
  async function onAdd() {
    const r = await addVenueToGroup({ data: { venue_email: email } });
    if ("error" in r) toast.error(r.error); else { toast.success("Venue added"); setEmail(""); void refresh(); }
  }
  async function onApply() {
    const r = await applyVenueGroupReferralCode({ data: { code } });
    if ("error" in r) toast.error(r.error); else { toast.success("Referral linked"); setCode(""); void refresh(); }
  }
  async function onRemove(venue_id: string) {
    const r = await removeVenueFromGroup({ data: { venue_id } });
    if ("error" in r) toast.error(r.error); else void refresh();
  }
  async function copy(value: string, label: string) {
    try { await navigator.clipboard.writeText(value); toast.success(`${label} copied`); }
    catch { toast.error("Couldn't copy. Long-press to copy manually."); }
  }

  if (loading) return <div className="p-8 text-muted-foreground">Loading…</div>;

  return (
    <div className="min-h-screen bg-background">
      <ReserveHeader />
      <div className="max-w-3xl mx-auto p-6 space-y-8">
        <div>
          <Link to="/venue" className="text-xs tracking-wide-luxury text-muted-foreground hover:text-foreground transition-colors">
            ← Back to dashboard
          </Link>
        </div>
      <header>
        <h1 className="font-display text-2xl">Group referrals</h1>
        <p className="text-muted-foreground text-sm mt-1">
          Refer another hospitality group's head office. When one of their venues completes its first paid shift,
          your group earns <strong>5 free booking credits</strong> (one platform fee waived each).
        </p>
      </header>

      {!status?.group ? (
        <section className="border border-border rounded p-4 space-y-3">
          <h2 className="font-medium">Step 1 — Name your group to get your referral code</h2>
          <p className="text-xs text-muted-foreground">
            We'll generate a unique <strong>GRP-XXXXXX</strong> code tied to your head office. Share it with other groups to earn credits,
            or apply someone else's code below once your group exists.
          </p>
          <input className="border border-border rounded px-3 py-2 w-full bg-background"
            placeholder="Group / head office name" value={name} onChange={(e) => setName(e.target.value)} />
          <button onClick={onCreate} disabled={name.trim().length < 2}
            className="bg-accent text-accent-foreground px-4 py-2 rounded disabled:opacity-50">Generate my referral code</button>
        </section>
      ) : (
        <>
          <section className="border border-border rounded p-4 space-y-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <h2 className="font-medium">{status.group.name}</h2>
              <div className="flex items-center gap-2">
                <code className="text-xs bg-muted px-2 py-1 rounded">{status.group.referral_code}</code>
                <button onClick={() => copy(status.group!.referral_code, "Code")} className="text-xs underline">Copy code</button>
              </div>
            </div>
            <div className="border border-border rounded bg-muted/40 p-3 text-xs flex items-center justify-between gap-2 flex-wrap">
              <span className="truncate">{shareUrl}</span>
              <button onClick={() => copy(shareUrl, "Referral link")} className="underline">Copy link</button>
            </div>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="border border-border rounded p-2">
                <div className="text-xs text-muted-foreground">Rewarded</div>
                <div className="text-lg font-display">{rewarded}</div>
              </div>
              <div className="border border-border rounded p-2">
                <div className="text-xs text-muted-foreground">Pending</div>
                <div className="text-lg font-display">{pending}</div>
              </div>
              <div className="border border-border rounded p-2">
                <div className="text-xs text-muted-foreground">Credits earned</div>
                <div className="text-lg font-display">{creditsEarned}</div>
              </div>
            </div>
          </section>

          <section className="border border-border rounded p-4 space-y-3">
            <h2 className="font-medium">Add a venue to your group</h2>
            <div className="flex gap-2">
              <input className="border border-border rounded px-3 py-2 flex-1 bg-background"
                placeholder="Venue account email" value={email} onChange={(e) => setEmail(e.target.value)} />
              <button onClick={onAdd} disabled={!email}
                className="bg-accent text-accent-foreground px-4 py-2 rounded disabled:opacity-50">Add</button>
            </div>
            <ul className="text-sm divide-y divide-border">
              {status.members.map((m) => (
                <li key={m.venue_id} className="flex items-center justify-between py-2">
                  <span>{m.name ?? m.venue_id}</span>
                  <button className="text-xs text-destructive" onClick={() => onRemove(m.venue_id)}>Remove</button>
                </li>
              ))}
              {status.members.length === 0 && <li className="text-muted-foreground py-2">No venues yet.</li>}
            </ul>
          </section>

          {!status.referred_by ? (
            <section className="border border-border rounded p-4 space-y-3">
              <h2 className="font-medium">Have a referral code?</h2>
              <p className="text-xs text-muted-foreground">
                Paste the <strong>GRP-XXXXXX</strong> code another group shared with you. When your first venue completes a paid shift,
                they'll earn credits — and you're linked to them for future perks.
              </p>
              <div className="flex gap-2">
                <input className="border border-border rounded px-3 py-2 flex-1 bg-background uppercase"
                  placeholder="Enter referral code (GRP-XXXXXX)" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} />
                <button onClick={onApply} disabled={code.trim().length < 4} className="bg-accent text-accent-foreground px-4 py-2 rounded disabled:opacity-50">Apply code</button>
              </div>
            </section>
          ) : (
            <section className="border border-border rounded p-4">
              <p className="text-sm">Referred by <strong>{status.referred_by.group_name}</strong> · status: {status.referred_by.status}</p>
            </section>
          )}

          <section className="border border-border rounded p-4">
            <h2 className="font-medium mb-3">Referrals sent</h2>
            <ul className="text-sm divide-y divide-border">
              {status.referrals_sent.map((r) => (
                <li key={r.id} className="py-2 flex items-center justify-between">
                  <div>
                    <div>{r.referred_group_name ?? r.id}</div>
                    <div className="text-xs text-muted-foreground">
                      {r.status === "rewarded" ? `Rewarded · +${r.reward_waivers_granted} credits` : r.status}
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground">{new Date(r.created_at).toLocaleDateString()}</div>
                </li>
              ))}
              {status.referrals_sent.length === 0 && <li className="text-muted-foreground py-2">No referrals yet — share your code above.</li>}
            </ul>
          </section>
        </>
      )}
      </div>
    </div>
  );
}
